import ButtonC, { BUTTON_HOVERSTYLE } from 'components/ButtonC';
import { LINK_TARGET } from 'components/Link';
import { CUSTOM_EVENTS, MINDSHIFT_LINKS } from 'helper/js/constants';
import { getCookie } from 'helper/js/helper';
import useEventListener from 'helper/js/hooks/use-event-listener';
import { useLanguage } from 'providers/Language';
import { TrackingContext } from 'providers/Tracking';
import TRACKING_EVENTS from 'providers/Tracking/constants';
import TRACKING_EVENT_COLLECTIONS from 'providers/Tracking/trackingEventCollections';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';

export default function Cart() {
  const { t } = useLanguage();
  const { trackEventUniversalAnalytics, trackEventDataLayerService } = useContext(TrackingContext);
  const initialCartCount = parseInt(getCookie('CART_QUANTITY') || '0', 10);
  const [cartCount, setCartCount] = useState(initialCartCount);
  const [ariaLiveMessage, setAriaLiveMessage] = useState('');
  const isInitialMount = useRef(true);

  const handleClick = () => {
    trackEventUniversalAnalytics(TRACKING_EVENTS.SHOPPING_CART_BUTTON_CLICKED);
    trackEventDataLayerService(TRACKING_EVENT_COLLECTIONS.SHOPPING_CART_BUTTON_CLICKED);
  };

  const updateCartCount = useCallback(() => {
    const newCartCount = parseInt(getCookie('CART_QUANTITY') || '0', 10);
    setCartCount(newCartCount);
  }, []);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    setAriaLiveMessage(
      `${t('shoppingCartAddedText')}. ${t('shoppingCartItemCount').replace(
        '%s',
        cartCount.toString(),
      )}`,
    );
    // After a short delay, clear the aria-live message
    setTimeout(() => {
      setAriaLiveMessage('');
    }, 3000);
  }, [cartCount, t]);

  useEffect(() => {
    updateCartCount();
  }, [updateCartCount]);

  // Listen to iframe's postMessage event for cart updates
  const handleIframeEvent = (event: MessageEvent) => {
    if (event.origin !== window.location.origin) return;
    if (event.data.event === CUSTOM_EVENTS.CART_ITEM_ADDED) {
      updateCartCount();
    }
  };

  useEventListener('message', handleIframeEvent);

  const cartAriaLabel = t('shoppingCartItemCount').replace('%s', cartCount.toString());

  return (
    <>
      <ButtonC
        href={MINDSHIFT_LINKS.CART}
        target={LINK_TARGET.SHOP}
        hoverStyle={BUTTON_HOVERSTYLE.ICON}
        inColumn
        ariaHidden={true}
        ariaLabel={cartAriaLabel}
        icon={`shopping-cart--${import.meta.env.VITE_THEME}`}
        label={t('shoppingCard')}
        onClick={handleClick}
        indicator={cartCount}
        transformLabelToUppercase={false}
        className="button--hide-label--sm"
      />
      <output className="sr-only" aria-live="polite" aria-atomic="true">
        {ariaLiveMessage}
      </output>
    </>
  );
}
